import React, { useMemo } from 'react';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { useTranslation } from 'react-i18next';
import { DateFormatter } from 'infrastructure/functions';
import { DateFormats } from 'infrastructure/enums';
import {
  useGetCcmInteractionTime,
  useGetRpmInteractionTime,
} from 'components/organisms/interactiveTime/hooks';
import { convertSecondsToTime } from 'utils/timeHelper';
import BaseLoader from 'components/atoms/base-loader';
import classNames from 'classnames';
import BoxArrowRightIcon from 'components/atoms/icons/box-arrow-right';
import { Link } from 'react-router-dom';
import { numberTypesDataSource } from 'infrastructure/data-sources/number-types';
import { rpmStatusesDataSource } from 'infrastructure/data-sources/rpm-statuses';
import { ccmStatusesDataSource } from 'infrastructure/data-sources/ccm-statuses';
import CopyableText from 'components/atoms/buttons/copyable-text';
import DoNotCallTag from 'components/atoms/tags/DoNotCallTag';
import { booleansDataSource } from 'infrastructure/data-sources/booleans';
import HoldingStatusLabel from 'components/atoms/patientStatus/holdiong-status-lable';
import { useClinicInfo } from 'infrastructure/hooks/clinic';
import { useDoctorInfo } from 'infrastructure/hooks/doctor';
import { fullNamePresenter } from 'utils';
import BaseDescriptionList from 'components/atoms/base-description/list';
import BaseDescriptionItem from 'components/atoms/base-description/item';
import { Routes } from 'router/routes';
import { useGetDoNotCallInfo } from 'infrastructure/hooks/control-panel/use-get-call-info';

import s from './styles.module.scss';

interface IProps {
  patientGuid: string;
}

const CallDashboardPatientInfoForm: React.FC<IProps> = ({ patientGuid }) => {
  const { t } = useTranslation();

  const { patientInfo, loading: isFetchingInfo } = usePatientInfo({
    patientGuid,
  });

  const { ccmInteractionTime, isFetching: isFetchCcm } =
    useGetCcmInteractionTime({
      patientGuid,
      enabled: !!patientGuid,
    });
  const { rpmInteractionTime, isFetching: isFetchRpm } =
    useGetRpmInteractionTime({
      patientGuid,
      enabled: !!patientGuid,
    });
  const { clinicInfo, loading: isFetchingClinic } = useClinicInfo({
    clinicGuid: patientInfo?.clinicGuid ?? '',
  });
  const { doctorInfo, doctorInfoLoading } = useDoctorInfo({
    doctorGuid: patientInfo?.doctorGuid,
  });

  const loading =
    isFetchingInfo ||
    isFetchCcm ||
    isFetchRpm ||
    isFetchingClinic ||
    doctorInfoLoading;

  const { doNotCallRpm, rpmRestrictionType, doNotCallCcm, ccmRestrictionType } =
    useGetDoNotCallInfo();

  const rpmStatus = rpmStatusesDataSource().findByField(
    'id',
    patientInfo?.status,
  );
  const ccmStatus = ccmStatusesDataSource().findByField(
    'id',
    patientInfo?.ccmStatus,
  );
  const pcmStatus = booleansDataSource().findByField(
    'id',
    patientInfo?.pcmStatus,
  );
  const interactionStatus = booleansDataSource().findByField(
    'id',
    patientInfo?.interactionStatus,
  );

  const providerServiceType = clinicInfo?.providerServiceType
    ? clinicInfo.providerServiceType.join('; ')
    : '-';
  const referralTypes = clinicInfo?.referralTypes
    ? clinicInfo.referralTypes.join('; ')
    : '-';
  const serviceDeliveryModel = clinicInfo?.serviceDeliveryModel ?? '-';
  const clinicName = clinicInfo?.legalname ?? '-';

  const physician = useMemo(
    () =>
      fullNamePresenter({
        firstName: doctorInfo?.firstName,
        middleName: doctorInfo?.middleName,
        lastName: doctorInfo?.lastName,
        title: doctorInfo?.title,
      }),
    [doctorInfo],
  );

  const numberTypeLabel = (val: string | null | undefined) =>
    numberTypesDataSource().findByField('id', val)?.name;

  return (
    <BaseLoader loading={loading}>
      <div>
        <div className={s.head}>
          <BaseDescriptionList>
            <BaseDescriptionItem
              term={t('labels.id')}
              description={
                <CopyableText value={patientGuid} label={patientGuid} />
              }
              className={s.field}
              termClassName={s.grey}
              descriptionClassName={s.dark}
            />
            <BaseDescriptionItem
              term={t('labels.fullName')}
              description={`${patientInfo?.loginInfo?.fullName}`}
              className={s.field}
              termClassName={s.grey}
              descriptionClassName={s.dark}
            />
            <BaseDescriptionItem
              term={t('labels.birthDate')}
              description={DateFormatter({
                date: patientInfo?.loginInfo?.birthDate,
                format: DateFormats['MM-dd-yyyy'],
              })}
              className={s.field}
              termClassName={s.grey}
              descriptionClassName={s.dark}
            />
            {doNotCallRpm && (
              <div className={s.field}>
                <DoNotCallTag
                  module="RPM"
                  restrictionType={rpmRestrictionType}
                />
              </div>
            )}
            {doNotCallCcm && (
              <div className={s.field}>
                <DoNotCallTag
                  module="CCM"
                  restrictionType={ccmRestrictionType}
                />
              </div>
            )}
          </BaseDescriptionList>
          <Link
            target="_blank"
            to={Routes.patientGuid(patientGuid)}
            className={s.link}
          >
            <BoxArrowRightIcon />
            {t('labels.openPatientChart')}
          </Link>
        </div>
        <BaseDescriptionList className={s.times}>
          <div className={s.time}>
            <BaseDescriptionItem
              term={t('labels.clinic')}
              description={convertSecondsToTime(rpmInteractionTime?.clinicTime)}
              className={s.item}
              termClassName={s.grey}
              descriptionClassName={s.dark}
            />
            <span className={classNames(s.grey, s.line)}>
              {t('labels.rpm')}
            </span>
            <BaseDescriptionItem
              term={t('labels.total')}
              description={convertSecondsToTime(rpmInteractionTime?.totalTime)}
              className={s.item}
              termClassName={s.grey}
              descriptionClassName={s.dark}
            />
          </div>
          <span className={s.title}>{t('labels.interactiveTime')}</span>
          <div className={s.time}>
            <BaseDescriptionItem
              term={t('labels.clinic')}
              description={convertSecondsToTime(ccmInteractionTime?.clinicTime)}
              className={s.item}
              termClassName={s.grey}
              descriptionClassName={s.dark}
            />
            <span className={classNames(s.grey, s.line)}>
              {t('labels.ccm')}
            </span>
            <BaseDescriptionItem
              term={t('labels.total')}
              description={convertSecondsToTime(ccmInteractionTime?.totalTime)}
              className={s.item}
              termClassName={s.grey}
              descriptionClassName={s.dark}
            />
          </div>
        </BaseDescriptionList>

        <BaseDescriptionList className={s.list}>
          <BaseDescriptionItem
            term={t('labels.rpmStatus')}
            description={
              <span style={{ color: rpmStatus?.color }}>{rpmStatus?.name}</span>
            }
            className={s.item}
            termClassName={s.dark}
            descriptionClassName={s.grey}
          />
          <BaseDescriptionItem
            term={t('labels.ccmStatus')}
            description={
              <span style={{ color: ccmStatus?.color }}>{ccmStatus?.name}</span>
            }
            className={s.item}
            termClassName={s.dark}
            descriptionClassName={s.grey}
          />
          <BaseDescriptionItem
            term={t('labels.pcmStatus')}
            description={
              <span style={{ color: pcmStatus?.color }}>{pcmStatus?.name}</span>
            }
            className={s.item}
            termClassName={s.dark}
            descriptionClassName={s.grey}
          />
          <BaseDescriptionItem
            term={t('labels.rpmMonthlyInteractionStatus')}
            description={
              <span style={{ color: pcmStatus?.color }}>
                {interactionStatus?.name}
              </span>
            }
            className={s.item}
            termClassName={s.dark}
            descriptionClassName={s.grey}
          />
          <BaseDescriptionItem
            term={t('labels.holdingStatus')}
            description={
              <HoldingStatusLabel status={patientInfo?.holdingStatus} />
            }
            className={s.item}
            termClassName={s.dark}
            descriptionClassName={classNames(s.grey, s.orange)}
          />
        </BaseDescriptionList>
        <BaseDescriptionList className={s.list}>
          <BaseDescriptionItem
            term={t('labels.providerServiceType')}
            description={providerServiceType}
            className={s.item}
            termClassName={s.grey}
            descriptionClassName={s.dark}
          />
          <BaseDescriptionItem
            term={t('labels.serviceDeliveryModel')}
            description={serviceDeliveryModel}
            className={s.item}
            termClassName={s.grey}
            descriptionClassName={s.dark}
          />
          <BaseDescriptionItem
            term={t('labels.clinic')}
            description={clinicName}
            className={s.item}
            termClassName={s.grey}
            descriptionClassName={s.dark}
          />
          <BaseDescriptionItem
            term={t('labels.physician')}
            description={physician}
            className={s.item}
            termClassName={s.grey}
            descriptionClassName={s.dark}
          />
          <BaseDescriptionItem
            term={t('labels.physician')}
            description={referralTypes}
            className={s.item}
            termClassName={s.grey}
            descriptionClassName={s.dark}
          />
        </BaseDescriptionList>
        <h3 className={s.title}>{t('labels.contacts')}</h3>
        <BaseDescriptionList className={s.list}>
          <div className={classNames(s.flex, s.item)}>
            <BaseDescriptionItem
              term={t('labels.primary')}
              description={patientInfo?.loginInfo?.phone || '-'}
              termClassName={s.grey}
              descriptionClassName={s.dark}
            />
            <BaseDescriptionItem
              term={t('labels.type')}
              description={numberTypeLabel(
                patientInfo?.loginInfo?.primaryNumberType,
              )}
              className={s.grey}
            />
          </div>
          <div className={classNames(s.flex, s.item)}>
            <BaseDescriptionItem
              term={t('labels.secondary')}
              description={patientInfo?.homeNumber || '-'}
              termClassName={s.grey}
              descriptionClassName={s.dark}
            />
            <BaseDescriptionItem
              term={t('labels.type')}
              description={numberTypeLabel(patientInfo?.secondaryNumberType)}
              className={s.grey}
            />
          </div>
          <BaseDescriptionItem
            term={t('labels.email')}
            description={patientInfo?.loginInfo?.email || '-'}
            className={s.item}
            termClassName={s.grey}
            descriptionClassName={s.dark}
          />
        </BaseDescriptionList>
      </div>
    </BaseLoader>
  );
};

export default CallDashboardPatientInfoForm;
